import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Truck,
  ShoppingCart,
  Wallet,
  Receipt,
  CreditCard,
  Calendar,
  ArrowUpRight,
  Building2,
  Banknote,
  Scale,
  Package,
  Wrench,
  CircleDollarSign,
  ArrowDownCircle,
  ArrowUpCircle,
  Printer,
  Bell,
  Clock,
  CheckCircle2,
  Target,
  CalendarDays,
} from "lucide-react";



const formatCurrency = (value) => {
  return `₺${(value || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`;
};

const PeriodCard = ({ title, data, icon: Icon, color = "blue", invertColors = false }) => {
  const colors = {
    blue: "bg-blue-600",
    green: "bg-emerald-600",
    red: "bg-red-600",
    amber: "bg-amber-600",
    purple: "bg-purple-600",
  };

  return (
    <Card className="overflow-hidden">
      <div className={`${colors[color]} text-white p-3`}>
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" />
          <span className="font-semibold text-sm">{title}</span>
        </div>
      </div>
      <CardContent className="p-0">
        <div className="grid grid-cols-3 divide-x divide-slate-100">
          <div className="p-3 text-center">
            <p className="text-xs text-slate-500 mb-1">Günlük</p>
            <p className={`font-bold tabular-nums text-sm ${invertColors ? (data?.daily >= 0 ? 'text-emerald-600' : 'text-red-600') : ''}`}>
              {formatCurrency(data?.daily)}
            </p>
          </div>
          <div className="p-3 text-center bg-slate-50">
            <p className="text-xs text-slate-500 mb-1">Aylık</p>
            <p className={`font-bold tabular-nums text-sm ${invertColors ? (data?.monthly >= 0 ? 'text-emerald-600' : 'text-red-600') : ''}`}>
              {formatCurrency(data?.monthly)}
            </p>
          </div>
          <div className="p-3 text-center">
            <p className="text-xs text-slate-500 mb-1">Yıllık</p>
            <p className={`font-bold tabular-nums text-sm ${invertColors ? (data?.yearly >= 0 ? 'text-emerald-600' : 'text-red-600') : ''}`}>
              {formatCurrency(data?.yearly)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BalanceCard = ({ title, value, icon: Icon, color = "blue", isNegative = false }) => {
  const colors = {
    blue: "text-blue-600 bg-blue-50",
    green: "text-emerald-600 bg-emerald-50",
    red: "text-red-600 bg-red-50",
    amber: "text-amber-600 bg-amber-50",
    purple: "text-purple-600 bg-purple-50",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${colors[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">{title}</p>
            <p className={`text-xl font-bold tabular-nums ${isNegative ? 'text-red-600' : ''}`}>
              {isNegative ? '-' : ''}{formatCurrency(Math.abs(value || 0))}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [detailedStats, setDetailedStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [detailedRes, statsRes, agendaRes] = await Promise.all([
        axios.get(`${API}/dashboard/detailed`),
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/agenda`),
      ]);
      setDetailedStats(detailedRes.data);
      setDashboardStats(statsRes.data);
      
      // Filter agenda items for today and tomorrow
      const today = new Date().toISOString().split("T")[0];
      const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
      const filteredAgenda = (agendaRes.data || []).filter(
        (item) => item.date === today || item.date === tomorrow
      );
      setAgendaItems(filteredAgenda);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const netBalance = detailedStats?.debt_balance?.net_balance || 0;
  const isPositive = netBalance >= 0;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 print:space-y-4" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl md:text-3xl font-bold text-slate-900"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Dashboard
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">İşletmenizin finansal özeti</p>
        </div>
        <Button variant="outline" onClick={handlePrint} className="no-print">
          <Printer className="h-4 w-4 mr-2" /> Yazdır
        </Button>
      </div>

      {/* Yıl Sonu Ciro Tahmini Kartı */}
      {detailedStats?.year_forecast && (
        <Card className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center">
                  <Target className="h-7 w-7" />
                </div>
                <div>
                  <p className="text-white/80 text-sm flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    {new Date().getFullYear()} Yıl Sonu Ciro Tahmini
                  </p>
                  <p className="text-3xl md:text-4xl font-bold tabular-nums">
                    {formatCurrency(detailedStats.year_forecast.year_end_forecast)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                <div className="text-center bg-white/10 rounded-lg p-3">
                  <p className="text-white/70 text-xs">Günlük Ortalama</p>
                  <p className="text-xl font-bold tabular-nums">
                    {formatCurrency(detailedStats.year_forecast.daily_avg_sales)}
                  </p>
                </div>
                <div className="text-center bg-white/10 rounded-lg p-3">
                  <p className="text-white/70 text-xs">Bugüne Kadar</p>
                  <p className="text-xl font-bold tabular-nums">
                    {formatCurrency(detailedStats.year_forecast.current_yearly_sales)}
                  </p>
                  <p className="text-white/60 text-xs">{detailedStats.year_forecast.days_since_year_start} gün</p>
                </div>
                <div className="text-center bg-white/10 rounded-lg p-3 col-span-2 md:col-span-1">
                  <p className="text-white/70 text-xs">Kalan Gün</p>
                  <p className="text-xl font-bold tabular-nums">
                    {detailedStats.year_forecast.days_remaining}
                  </p>
                  <p className="text-white/60 text-xs">yıl sonuna</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today & Tomorrow Agenda */}
      {agendaItems.length > 0 && (
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="h-5 w-5 text-amber-600" />
              <h3 className="font-semibold text-amber-800">Ajanda Hatırlatıcısı</h3>
              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                {agendaItems.length} kayıt
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {agendaItems.map((item) => {
                const isToday = item.date === new Date().toISOString().split("T")[0];
                return (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      isToday 
                        ? "bg-white border-l-4 border-amber-500 shadow-sm" 
                        : "bg-white/60 border-l-4 border-orange-300"
                    }`}
                  >
                    <div className={`p-2 rounded-full ${isToday ? "bg-amber-100" : "bg-orange-100"}`}>
                      {item.completed ? (
                        <CheckCircle2 className={`h-4 w-4 ${isToday ? "text-emerald-600" : "text-emerald-500"}`} />
                      ) : (
                        <Clock className={`h-4 w-4 ${isToday ? "text-amber-600" : "text-orange-500"}`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                          isToday 
                            ? "bg-amber-100 text-amber-700" 
                            : "bg-orange-100 text-orange-700"
                        }`}>
                          {isToday ? "Bugün" : "Yarın"}
                        </span>
                        {item.reminder && (
                          <Bell className="h-3 w-3 text-amber-500" />
                        )}
                      </div>
                      <p className={`font-medium text-sm mt-1 truncate ${item.completed ? "line-through text-slate-400" : "text-slate-800"}`}>
                        {item.title}
                      </p>
                      {item.description && (
                        <p className="text-xs text-slate-500 truncate mt-0.5">{item.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Net Balance Hero */}
      <Card className={`${isPositive ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-red-600'} text-white`}>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center">
                <Scale className="h-7 w-7" />
              </div>
              <div>
                <p className="text-white/80 text-sm">Borç-Alacak Dengesi</p>
                <p className="text-3xl font-bold tabular-nums">
                  {formatCurrency(Math.abs(netBalance))}
                </p>
                <p className="text-white/80 text-sm mt-0.5">
                  {isPositive ? '✓ Pozitif Denge' : '⚠ Negatif Denge'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-white/70 text-xs">Alacaklar</p>
                <p className="text-lg font-bold tabular-nums">
                  {formatCurrency(detailedStats?.debt_balance?.receivables)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-xs">Borçlar</p>
                <p className="text-lg font-bold tabular-nums">
                  {formatCurrency(detailedStats?.debt_balance?.payables)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-xs">Nakit</p>
                <p className="text-lg font-bold tabular-nums">
                  {formatCurrency(detailedStats?.current_balances?.total_liquid)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-xs">K.Kartı Borcu</p>
                <p className="text-lg font-bold tabular-nums">
                  {formatCurrency(detailedStats?.current_balances?.credit_card_debt)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Balances */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <BalanceCard
          title="Kasa Bakiyesi"
          value={detailedStats?.current_balances?.cash}
          icon={Banknote}
          color="green"
        />
        <BalanceCard
          title="Banka Bakiyesi"
          value={detailedStats?.current_balances?.bank}
          icon={Building2}
          color="blue"
        />
        <BalanceCard
          title="Kredi Kartı Borcu"
          value={detailedStats?.current_balances?.credit_card_debt}
          icon={CreditCard}
          color="red"
          isNegative={true}
        />
      </div>

      {/* Period Stats */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Dönemsel Özet</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <PeriodCard
            title="Ciro (Satışlar)"
            data={detailedStats?.revenue}
            icon={TrendingUp}
            color="green"
          />
          <PeriodCard
            title="Toplam Alışlar"
            data={detailedStats?.purchases?.combined}
            icon={ShoppingCart}
            color="red"
          />
          <PeriodCard
            title="Giderler"
            data={detailedStats?.expenses}
            icon={Receipt}
            color="amber"
          />
          <PeriodCard
            title="Kar"
            data={detailedStats?.profit}
            icon={CircleDollarSign}
            color="blue"
            invertColors={true}
          />
          <PeriodCard
            title="Tahsilatlar"
            data={detailedStats?.collections}
            icon={ArrowDownCircle}
            color="green"
          />
          <PeriodCard
            title="Ödemeler"
            data={detailedStats?.payments}
            icon={ArrowUpCircle}
            color="purple"
          />
        </div>
      </div>

      {/* Purchase Details */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Alış Detayları</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PeriodCard
            title="Mal Alışları"
            data={detailedStats?.purchases?.goods}
            icon={Package}
            color="red"
          />
          <PeriodCard
            title="Hizmet Alışları"
            data={detailedStats?.purchases?.services}
            icon={Wrench}
            color="amber"
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Son Satışlar</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardStats?.recent_sales?.length > 0 ? (
              <div className="space-y-2">
                {dashboardStats.recent_sales.map((sale) => (
                  <div
                    key={sale.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 text-sm truncate">{sale.customer_name}</p>
                      <p className="text-xs text-slate-500 truncate">{sale.invoice_no}</p>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <p className="font-semibold text-emerald-600 tabular-nums text-sm">
                        {formatCurrency(sale.total)}
                      </p>
                      <p className="text-xs text-slate-500">{sale.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8 text-sm">Henüz satış yok</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Bugünkü Görevler</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardStats?.agenda_items?.length > 0 ? (
              <div className="space-y-2">
                {dashboardStats.agenda_items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <Calendar className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate text-sm">{item.title}</p>
                      {item.time && <p className="text-xs text-slate-500">{item.time}</p>}
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8 text-sm">Bugün için görev yok</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Debt/Receivable Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 text-center">
            <Users className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-700 tabular-nums">
              {formatCurrency(detailedStats?.debt_balance?.receivables)}
            </p>
            <p className="text-xs text-emerald-600">Müşteri Alacakları</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 text-center">
            <Truck className="h-6 w-6 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-700 tabular-nums">
              {formatCurrency(detailedStats?.debt_balance?.payables)}
            </p>
            <p className="text-xs text-red-600">Tedarikçi Borçları</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <Wallet className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-700 tabular-nums">
              {formatCurrency(detailedStats?.current_balances?.total_liquid)}
            </p>
            <p className="text-xs text-blue-600">Toplam Nakit</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 text-center">
            <CreditCard className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-700 tabular-nums">
              -{formatCurrency(detailedStats?.current_balances?.credit_card_debt)}
            </p>
            <p className="text-xs text-purple-600">K.Kartı Borcu</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
